import type { CanonicalExperienceCapability } from "@src/packages/experience/canonicalExperienceContext";
import type { ExperienceContext } from "@src/packages/experience/experience.types";

export type DocumentSlotContext = {
  treatmentIntents: readonly string[] | null;
  partnerChoices: readonly string[] | null;
};

type DocumentTreatmentIntent =
  | "Preservation"
  | "Donation"
  | "Conception"
  | "StartingTreatment"
  | "InTreatment";

export type AuthorizedDocumentExperience = {
  status: "loading" | "absent" | "ready" | "error";
  context: ExperienceContext | null;
  capability: CanonicalExperienceCapability | null;
};

function treatmentIntentForContext(
  context: ExperienceContext,
): DocumentTreatmentIntent | null {
  if (context.treatment.hasActiveCycle) return "InTreatment";

  if (
    context.familyBuildingPlan.primaryIntent === "preserve" ||
    context.familyBuildingPlan.treatmentGoal === "egg-freezing" ||
    context.familyBuildingPlan.treatmentGoal === "sperm-freezing" ||
    context.familyBuildingPlan.treatmentGoal === "embryo-freezing"
  ) {
    return "Preservation";
  }

  if (context.familyBuildingPlan.primaryIntent === "donate") {
    return "Donation";
  }

  if (context.lifecycle.journeyStage === "trying-naturally") {
    return "Conception";
  }

  if (
    context.familyBuildingPlan.treatmentGoal !== "unknown" &&
    context.familyBuildingPlan.primaryIntent === "grow"
  ) {
    return "StartingTreatment";
  }

  return null;
}

/**
 * Use known journey context to order the document experience, but keep every
 * slot available while the user is still documenting their journey. Missing
 * context is an invitation to choose a document type, not a permission gate.
 */
export function filterDocumentSlotsForExperience<T extends DocumentSlotContext>(
  slots: readonly T[],
  experience: AuthorizedDocumentExperience,
): T[] {
  const treatmentIntent = experience.context
    ? treatmentIntentForContext(experience.context)
    : null;
  const partnerChoice =
    experience.context?.familyBuildingPlan.partnerChoice ?? null;

  return slots.filter((slot) => {
    if (slot.treatmentIntents === null && slot.partnerChoices === null) {
      return true;
    }
    if (
      slot.treatmentIntents !== null &&
      treatmentIntent !== null &&
      !slot.treatmentIntents.includes(treatmentIntent)
    ) {
      return false;
    }
    if (slot.partnerChoices !== null) {
      if (
        partnerChoice !== null &&
        !slot.partnerChoices.includes(partnerChoice)
      ) {
        return false;
      }
    }
    return true;
  });
}
